// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import {
	Account,
	AccountEntityPayload,
	AccountPayload,
	Country,
	Entity,
	ErrorCause,
	Feedback,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Box, Grid, InputAdornment, SelectChangeEvent } from '@mui/material';
import { LockOutlined as IconLock } from '@mui/icons-material';

// fields
import { Checkbox } from '../../fields/Checkbox/Checkbox';
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';
import { SelectCountry } from '../../fields/SelectCountry/SelectCountry';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';
import { ListItemTextAccount } from '../../ui/ListItemTextAccount/ListItemTextAccount';
import { Loading } from '../../ui/Loading/Loading';

// utils
import { translateError, translateEntityType } from '../../../utils/translations';

// props
export interface FormEditAccountProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditAccount = ({ onClose, onSuccess, setIsLoading }: FormEditAccountProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [account, setAccount] = useState<Account | null>(null);
	const [accounts, setAccounts] = useState<Account[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [searchParams] = useSearchParams();
	const [values, setValues] = useState({
		aba: '',
		accounts: [] as number[],
		country: '',
		entities: [] as string[],
		holder: '',
		institution: '',
		isReimbursement: false,
		name: '',
		num: '',
		owner: '',
		postcode: '',
		routeCode: '',
		sortCode: '',
		state: '',
		street: '',
		swiftCode: '',
		town: '',
	});

	// params
	const id = searchParams.get('dialogAccount');

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);

	// vars
	const idForm = 'form-edit-account';
	const isDesignated = account?.type === 'DESIGNATED';
	const isLocal = account?.type === 'LOCAL';
	const ownerFound = entities.find((entity) => entity.id === Number(values.owner));

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch account
	const { isLoading: isLoadingAccount } = useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/accounts/${id}?populate=accounts`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setAccount(res.data[0]);
			}
		},
	});

	// fetch designated accounts for local account selection
	useFetch({
		isEnabled: Boolean(project && account?.type === 'LOCAL'),
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&populate=loan&type=DESIGNATED`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setAccounts(res.data);
		},
	});

	// fetch entities
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name,type,wallet&type=BORROWER,INTERMEDIARY,PIU,SUB_PIU&populate=wallet`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// options
	const optionsAccounts = accounts.map((account) => {
		return {
			label: <ListItemTextAccount account={account} />,
			secondary: account.institution,
			value: account.id,
			MenuItemProps: {
				resource: account,
			},
		};
	});

	const optionsEntities = entities
		.map((entity) => {
			return {
				disabled: entity.wallet?.status === 'INACTIVE',
				label: entity.name,
				secondary: translateEntityType(entity.type, t),
				value: String(entity.id),
			};
		})
		.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

	// filter out owner for additional entities
	const optionsEntitiesAdditional = optionsEntities.filter(
		(entity) => entity.value !== values.owner
	);

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		try {
			// clear feedback
			setFeedback(null);

			// set loading
			setIsLoading(true);

			if (!project) {
				throw new Error('The account needs to have a project ID associated with it.', {
					cause: {
						id: 'ACCOUNT_PROJECT_NON_EXISTENT',
					},
				});
			}

			// define entities
			let entities: AccountEntityPayload[] = [];

			if (values.entities) {
				entities = values.entities.map((id) => {
					return {
						id: Number(id),
						relationship: 'ASSOCIATE',
					};
				});
			}

			// check if OWNER entity is associated entity
			const ownerFound = entities.find((entity) => entity.id === Number(values.owner));

			if (ownerFound) {
				throw new Error('The account owner cannot be an associated entity.', {
					cause: {
						id: 'ACCOUNT_OWNER_ASSOCIATE',
					},
				});
			}

			const body: AccountPayload = {
				country: Number(values.country),
				entities,
				institution: String(values.institution),
				isReimbursement: values.isReimbursement,
				name: String(values.name),
				num: String(values.num),
				postcode: String(values.postcode),
				project: project.id,
				state: values.state,
				street: values.street,
				town: values.town,
			};

			if (values.aba) {
				body.aba = values.aba;
			}

			if (Array.isArray(values.accounts)) {
				body.accounts = values.accounts.map((id) => Number(id));
			}

			if (values.holder) {
				body.holder = values.holder;
			}

			if (values.routeCode) {
				body.routeCode = values.routeCode;
			}

			if (values.sortCode) {
				body.sortCode = values.sortCode;
			}

			if (values.swiftCode) {
				body.swiftCode = values.swiftCode;
			}

			const fetchEditAccount = await fetch(`${process.env.REACT_APP_API_URL}/accounts/${id}`, {
				method: 'PATCH',
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resEditAccount = await fetchEditAccount.json();

			if (resEditAccount.error) {
				throw new Error(resEditAccount.error.message, {
					cause: {
						id: resEditAccount.error.id,
						details: resEditAccount.error?.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.accountUpdated', { name: values.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			if (cause) {
				if (cause.id === 'ACCOUNT_NUMBER_DUPLICATE') {
					cause.details.num = values.num;
				}
				if (cause.id === 'DESIGNATED_ACCOUNT_HAS_TRANSACTIONS') {
					cause.details.accountName = values.name;
				}

				if (cause.id === 'ACCOUNT_INVALID_ENTITY_TRANSACTIONS') {
					const entity = entities.find((entity) => entity.id === cause.details?.entity?.id);
					cause.details.entity = entity;
				}
				if (cause.id === 'ACCOUNT_NON_EXISTENT') {
					cause.details.id = account?.id;
				}
			}

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});
		} finally {
			// set loading
			setIsLoading(false);
			// set feedback
			setFeedback(feedbackNew);
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	// content
	const renderContent = () => {
		if (isLoadingAccount) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				{!isDesignated && (
					<Grid item xs={12}>
						<Checkbox
							id={`${idForm}-is-reimbursement`}
							checked={values.isReimbursement}
							label={t('reimbursementAccount')}
							name="isReimbursement"
							onChange={() => {
								setValues((valuesPrev) => {
									return {
										...valuesPrev,
										isReimbursement: !valuesPrev.isReimbursement,
									};
								});
							}}
						/>
					</Grid>
				)}
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-number`}
						label={t('accountNumber')}
						name="num"
						onChange={handleChangeValue}
						value={values.num}
					/>
				</Grid>
				{isDesignated && (
					<Grid item xs={12}>
						<Input
							id={`${idForm}-holder`}
							label={t('accountHolder')}
							name="holder"
							onChange={handleChangeValue}
							required={true}
							value={values.holder}
						/>
					</Grid>
				)}
				<Grid item xs={12} md={12}>
					<Box
						sx={(theme) => {
							return {
								backgroundColor: theme.palette.brand.grey[200],
								borderRadius: 2,
								padding: theme.spacing(2),
							};
						}}>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<Input
									id={`${idForm}-institution`}
									label={t('financialInstitution')}
									name="institution"
									onChange={handleChangeValue}
									required={true}
									value={values.institution}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${idForm}-street`}
									label={t('street')}
									name="street"
									onChange={handleChangeValue}
									required={true}
									value={values.street}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<SelectCountry
									id={`${idForm}-country`}
									label={t('country')}
									name="country"
									onChange={handleChangeValue}
									required={true}
									value={values.country}
									valueType="ID"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${idForm}-town`}
									label={t('town')}
									name="town"
									onChange={handleChangeValue}
									required={true}
									value={values.town}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${idForm}-state`}
									label={t('state')}
									name="state"
									onChange={handleChangeValue}
									value={values.state}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${idForm}-postcode`}
									label={t('postcode')}
									name="postcode"
									onChange={handleChangeValue}
									required={true}
									value={values.postcode}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${idForm}-swift-code`}
									label={t('swiftCode')}
									name="swiftCode"
									onChange={handleChangeValue}
									required={true}
									value={values.swiftCode}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${idForm}-aba`}
									label={t('aba')}
									name="aba"
									onChange={handleChangeValue}
									value={values.aba}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${idForm}-route-code`}
									label={t('routeCode')}
									name="routeCode"
									onChange={handleChangeValue}
									value={values.routeCode}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${idForm}-sort-code`}
									label={t('sortCode')}
									name="sortCode"
									onChange={handleChangeValue}
									value={values.sortCode}
								/>
							</Grid>
						</Grid>
					</Box>
				</Grid>
				{isLocal && (
					<Grid item xs={12}>
						<Select
							id={`${idForm}-designated-accounts`}
							label={t('designatedAccounts')}
							multiple={true}
							name="accounts"
							onChange={handleChangeValue}
							options={optionsAccounts}
							value={values.accounts}
						/>
					</Grid>
				)}

				<Grid item xs={12}>
					<Input
						id={`${idForm}-owner`}
						InputProps={{
							readOnly: true,
							endAdornment: (
								<InputAdornment position="end">
									<IconLock />
								</InputAdornment>
							),
						}}
						label={t('owner')}
						name="owner"
						required={true}
						value={ownerFound?.name}
					/>
				</Grid>

				{optionsEntitiesAdditional.length > 0 && (
					<Grid item xs={12}>
						<Select
							disabled={values.owner === ''}
							hasNoneOption={false}
							helperText={t('additionalEntities.helper')}
							id={`${idForm}-entities`}
							label={t('additionalEntities')}
							multiple={true}
							name="entities"
							onChange={handleChangeValue}
							options={optionsEntitiesAdditional}
							value={values.entities}
						/>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</>
		);
	};

	// set default values when account changes
	useEffect(() => {
		if (account) {
			const defaultEntities = account?.entities
				?.filter((item) => item.relationship === 'ASSOCIATE')
				.map((item) => String(item.entity));

			const ownerEntity = account?.entities?.find((item) => item.relationship === 'OWNER');

			const defaultAccounts =
				account?.accounts?.map((acc) => (typeof acc === 'number' ? acc : acc.id)) || [];

			setValues({
				aba: account.aba || '',
				accounts: defaultAccounts,
				country: String((account.country as Country)?.id || ''),
				entities: defaultEntities || [],
				holder: account.holder || '',
				institution: account.institution,
				isReimbursement: account.isReimbursement || false,
				name: account.name || '',
				num: account.num || '',
				owner: ownerEntity ? String(ownerEntity.entity) : '',
				postcode: account.postcode || '',
				routeCode: account.routeCode || '',
				sortCode: account.sortCode || '',
				state: account.state || '',
				street: account.street || '',
				swiftCode: account.swiftCode || '',
				town: account.town || '',
			});
		}
	}, [account]);

	return (
		<form id={`${idForm}`} name="formEditAccount" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</form>
	);
};
